import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import cache from "../utils/cache";
import Linkify from "linkify-react";
import { normalizeVendorName, isVendorMatch, getRewardLabel } from "../utils/vendorMatchUtils";

// React Icons
import {
  FaWhatsapp,
  FaTelegramPlane,
  FaSms,
  FaEnvelope,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLink,
  FaCopy,
} from "react-icons/fa";


async function loadAllVendors() {
  let cachedVendors = cache.getData().vendors;
  if (cachedVendors && cachedVendors.length > 0) {
    return cachedVendors;
  }

  try {
    const vendorsCollection = collection(db, "vendors");
    const snapshot = await getDocs(vendorsCollection);

    const groupedDocs = snapshot.docs.filter((doc) =>
      doc.id.startsWith("vendors_")
    );

    const allVendors = [];
    groupedDocs.forEach((doc) => {
      const data = doc.data();
      if (Array.isArray(data.vendors)) {
        data.vendors.forEach((vendor) => {
          // Fix missing vendorName from Vendors.js approach
          if (!vendor.vendorName && vendor.id) {
            vendor.vendorName = vendor.id;
          }
          allVendors.push(vendor);
        });
      }
    });

    cache.vendors = allVendors; // cache for future calls

    return allVendors;
  } catch (error) {
    console.error("Error loading vendors:", error);
    return [];
  }
}

async function getTrackingIdForVendor(vendorName) {
  const allVendors = await loadAllVendors();

  const normVendor = normalizeVendorName(vendorName);
  const matchedVendor = allVendors.find((v) =>
    isVendorMatch(normalizeVendorName(v.vendorName || v.id), normVendor)
  );

  return matchedVendor?.tracking_id || null;
}


function ShareButtons({ url, title, offer }) {
  const [showOptions, setShowOptions] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!offer) return null;

  const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);

  const baseUrl = url.split("?")[0].split("#")[0]; // base path without any params or hashes
  const cleanUrl = `${baseUrl}?source=share`;

  const buildWhatsAppText = () => {
    if (!offer) return "";

    const cleanPrice = Number(offer?.offerPrice || 0);
    const originalPrice = Number(offer?.originalPrice || 0);
    const vendorText = offer?.vendor ? ` (${offer.vendor})` : "";

    // 🧩 Emoji map based on offerType
    const emojiMap = {
      "Deal of the Day": "🎯",     // target
      "Black Friday Deals": "🔥",  // fire
      "Clearance": "✨",           // sparkle
      "Lightning Deal": "⚡",      // lightning
      "Limited time deal": "⏰",        // clock / time
      "Doorbusters": "🚪💥",   // door + burst combo
    };

    // 🏷️ default emoji (price tag)
    const emoji =
      (offer?.offerType &&
        Object.keys(emojiMap).find(
          (key) => offer.offerType.toLowerCase() === key.toLowerCase()
        ) &&
        emojiMap[
        Object.keys(emojiMap).find(
          (key) => offer.offerType.toLowerCase() === key.toLowerCase()
        )
        ]) ||
      "🏷️";

    let message = "";

    // 🎯 Intro — depends on offerType
    if (offer?.offerType) {
      message += `${emoji} ${offer.offerType}${vendorText}\n`;
    } else {
      message += `Hey! ${emoji} Check out this new deal${vendorText}:\n`;
    }

    // 🛒 Product name
    message += `*${offer?.productName}*\n`;

    // 💰 Deal Price
    if (cleanPrice > 0) {
      let line = `💰 *Deal Price:* $${cleanPrice}`;
      if (originalPrice > 0 && originalPrice > cleanPrice) {
        const discount = Math.round(
          ((originalPrice - cleanPrice) / originalPrice) * 100
        );
        line += `  ~$${originalPrice}~  (${discount}% OFF)`;
      }
      message += line + `\n`;
    } else {
      message += `💰 *Deal Price:* Free 💸\n`;
    }

    // 🏬 Free store pickup / shipping
    const perks = [];
    if (offer?.freeStorePickup === "Yes") perks.push("🏬 Free Store Pickup");
    if (offer?.freeShippingMinimum === 0) perks.push("🚚 Free Shipping");
    if (perks.length > 0) message += perks.join(" | ") + "\n";

    // 🔗 Link
    message += `*Link:* ${cleanUrl}`;

    // 📌 Optional note
    if (offer.instructions?.trim()) {
      message += `\n\n📌 *Note:* Read the instructions for how to use the offer.`;
    }

    return message;
  };


  const buildPlainText = () => {
    let priceText = offer.offerPrice === 0 ? "Free" : `$${offer.offerPrice}`;

    if (offer.cashback && offer.cashback > 0) {
      priceText += ` After $${offer.cashback} Cashback`;
    }

    let message =
      `Hey! Check out this new deal:\n` +
      `${offer.productName}\n`;

    if (offer.originalPrice) {
      message += `Original Price: $${offer.originalPrice}\n`;
    }

    message += `Offer Price: ${priceText}\n` +
      `Link: ${cleanUrl}`;

    if (offer.instructions?.trim()) {
      message += `\n\nNote: Read the instructions for how to use the offer.`;
    }

    return message;
  };


  const buildTelegramText = () => {
    if (!offer) return "";

    // 🎯 Emoji map for offer types
    const emojiMap = {
      "Deal of the Day": "🎯",
      "Black Friday Deals": "🔥",
      "Clearance": "✨",
      "Lightning Deal": "⚡",
      "Limited Time Deal": "⏰",
      "Doorbusters": "💥",
    };

    const emojiKey = Object.keys(emojiMap).find(
      (key) => offer?.offerType?.toLowerCase() === key.toLowerCase()
    );
    const emoji = emojiKey ? emojiMap[emojiKey] : "🏷️";
    const vendorText = offer?.vendor ? ` (${offer.vendor})` : "";

    // 🏁 Header
    let message = "";
    if (offer?.offerType) {
      message += `${emoji} ${offer.offerType}${vendorText}\n`;
    } else {
      message += `Hey! ${emoji} Check out this new deal${vendorText}:\n`;
    }

    // 🛒 Product name
    message += `${offer.productName}\n`;

    // 💰 Price line
    const offerPrice = Number(offer.offerPrice || 0);
    const originalPrice = Number(offer.originalPrice || 0);
    let priceLine = "";

    if (offerPrice === 0) {
      priceLine = `💰Deal Price: Free 💸`;
    } else {
      priceLine = `💰Deal Price: $${offerPrice}`;
      if (originalPrice > offerPrice) {
        const discount = Math.round(((originalPrice - offerPrice) / originalPrice) * 100);
        const strikeOriginal = [...`$${originalPrice}`].map((c) => c + "\u0336").join("");
        priceLine += `  ${strikeOriginal}  (${discount}% OFF)`;
      }
      if (offer.cashback && offer.cashback > 0) {
        priceLine += ` After $${offer.cashback} Cashback`;
      }
    }

    message += priceLine + "\n";

    // 🏬 Perks
    const perks = [];
    if (offer?.freeStorePickup === "Yes") perks.push("🏬 Free Store Pickup");
    if (offer?.freeShippingMinimum === 0) perks.push("🚚 Free Shipping");
    if (perks.length > 0) message += perks.join(" | ") + "\n";

    // 📌 Optional note
    if (offer.instructions?.trim()) {
      message += `\n📌 Note: Read the instructions for how to use the offer.`;
    }

    return message.trim();
  };


  const plainText = buildPlainText();
  const whatsappText = buildWhatsAppText();

  const encodedWhatsAppText = encodeURIComponent(whatsappText);
  const encodedPlainText = encodeURIComponent(plainText);
  const encodedTitle = encodeURIComponent(title || "New Deal");
  const encodedTelegramText = encodeURIComponent(buildTelegramText());

  const whatsappUrl = isMobile
    ? `https://wa.me/?text=${encodedWhatsAppText}`
    : `https://web.whatsapp.com/send?text=${encodedWhatsAppText}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(cleanUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-4 relative flex flex-wrap items-center gap-2 px-0 sm:px-0">
      {/* Share button */}
      <button
        type="button"
        aria-label="Toggle share options"
        onClick={() => setShowOptions((prev) => !prev)}
        className="flex items-center space-x-2 bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
      >
        <FaLink size={20} />
        <span>Share</span>
      </button>

      {/* Share icons inline */}
      {showOptions && (
        <div className="flex flex-wrap gap-2 sm:ml-4">
          {/* Copy Link */}
          <div className="relative">
            <button
              onClick={copyToClipboard}
              type="button"
              aria-label="Copy link"
              title="Copy Link"
              className="p-3 rounded-full bg-gray-200 hover:bg-gray-300"
            >
              <FaCopy size={22} />
            </button>
            {copied && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full mb-1 text-xs text-green-600 animate-fade bg-white px-1 rounded shadow">
                Link copied!
              </span>
            )}
          </div>

          {/* WhatsApp */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on WhatsApp"
            title="WhatsApp"
            className="p-3 rounded-full bg-green-500 text-white hover:bg-green-600"
          >
            <FaWhatsapp size={22} />
          </a>

          {/* Telegram */}
          <a
            href={`https://t.me/share/url?url=${encodeURIComponent(cleanUrl)}&text=${encodedTelegramText}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Telegram"
            title="Telegram"
            className="p-3 rounded-full bg-cyan-600 text-white hover:bg-cyan-700"
          >
            <FaTelegramPlane size={22} />
          </a>

          {/* SMS */}
          <a
            href={`sms:?&body=${encodedPlainText}`}
            aria-label="Send SMS"
            title="SMS"
            className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600"
          >
            <FaSms size={22} />
          </a>

          {/* Email */}
          <a
            href={`mailto:?subject=${encodedTitle}&body=${encodedPlainText}`}
            aria-label="Send Email"
            title="Email"
            className="p-3 rounded-full bg-red-500 text-white hover:bg-red-600"
          >
            <FaEnvelope size={22} />
          </a>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cleanUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            title="Facebook"
            className="p-3 rounded-full bg-blue-700 text-white hover:bg-blue-800"
          >
            <FaFacebook size={22} />
          </a>

          {/* Twitter */}
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodeURIComponent(cleanUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Twitter"
            title="Twitter"
            className="p-3 rounded-full bg-black text-white hover:bg-gray-800"
          >
            <FaTwitter size={22} />
          </a>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Instagram"
            title="Instagram"
            className="p-3 rounded-full bg-pink-500 text-white hover:bg-pink-600"
          >
            <FaInstagram size={22} />
          </a>
        </div>
      )}

      <style>{`
    @keyframes fadeOut {
      0% { opacity: 1; }
      90% { opacity: 1; }
      100% { opacity: 0; }
    }
    .animate-fade {
      animation: fadeOut 2s forwards;
    }
  `}</style>
    </div>
  );

}

function ProductOfferDetailSkeleton() {
  return (
    <div className="p-4 max-w-6xl mx-auto animate-pulse">
      <div className="h-8 bg-gray-300 rounded w-2/3 mb-4" />
      <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
      <div className="h-6 bg-gray-200 rounded w-1/4 mb-6" />

      <div className="w-full h-64 bg-gray-200 rounded mb-6" />

      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-5/6" />
        <div className="h-4 bg-gray-200 rounded w-4/6" />
        <div className="h-4 bg-gray-200 rounded w-3/6" />
      </div>

      <div className="mt-6 w-40 h-10 bg-gray-300 rounded" />
    </div>
  );
}


export default function ProductOfferDetail() {
  const [notFound, setNotFound] = useState(false);
  const { productOfferId } = useParams();
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState(null);

  const [offer, setOffer] = useState(null);
  const [matchedBankOffers, setMatchedBankOffers] = useState([]);
  const [banksLogos, setBanksLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBankOfferPopup, setShowBankOfferPopup] = useState(false);
  const [selectedBankOffer, setSelectedBankOffer] = useState(null);

  const [showFullInstructions, setShowFullInstructions] = useState(false);
  const [copiedPromo, setCopiedPromo] = useState(false);

  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const isShared = urlParams.get("source") === "share";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!productOfferId) {
      setNotFound(true);
      return;
    }

    async function fetchData() {
      setLoading(true);
      try {
        // Load product offers from cache or Firestore
        let allProductOffers = cache.allProductOffers;
        if (!allProductOffers) {
          const productSnap = await getDocs(collection(db, "product_offers"));
          allProductOffers = [];

          productSnap.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.offers)) {
              data.offers.forEach((offer) => {
                allProductOffers.push({
                  ...offer,
                  id: `${doc.id}_${offer.offerId || Math.random().toString(36).substring(2, 8)}`,
                });
              });
            }
          });

          cache.allProductOffers = allProductOffers;
        }

        const matchedOffer = allProductOffers.find((o) => o.id === productOfferId);
        if (!matchedOffer) {
          setNotFound(true);
          return;
        }

        // Check if offer is expired
        const expiry = matchedOffer.endDttm;
        if (expiry) {
          const expiryDate = expiry.toDate ? expiry.toDate() : new Date(expiry);

          // Convert to Central Time (CT)
          const now = new Date();
          const nowCT = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
          if (expiryDate < nowCT) {
            setNotFound(true);
            return;
          }
        }

        setOffer(matchedOffer);


        // After you have matchedOffer loaded and setOffer(matchedOffer)
        const trackingId = await getTrackingIdForVendor(matchedOffer.vendor);
        setTrackingId(trackingId);

        // Load bank offers from cache or Firestore
        let allBankOffers = cache.allBankOffers;
        if (!allBankOffers) {
          const bankOffersSnap = await getDocs(collection(db, "bank_offers"));
          allBankOffers = [];
          bankOffersSnap.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.offers)) {
              data.offers.forEach((offer) => {
                allBankOffers.push({ ...offer, bankName: doc.id });
              });
            }
          });
          cache.allBankOffers = allBankOffers;
        }

        // Load bank logos from cache or Firestore
        let logosMap = cache.banksLogos;
        if (!logosMap) {
          const banksSnap = await getDocs(collection(db, "banks"));
          logosMap = {};
          banksSnap.forEach((doc) => {
            const data = doc.data();
            logosMap[doc.id.toLowerCase()] = data.logo || "";
          });
          cache.banksLogos = logosMap;
        }
        setBanksLogos(logosMap);

        // Use vendorMatchUtils to normalize and match
        const pNorm = normalizeVendorName(matchedOffer.vendor);
        const matched = allBankOffers.filter((b) => {
          if (!b.vendor) return false;
          const bNorm = normalizeVendorName(b.vendor);
          return isVendorMatch(bNorm, pNorm);
        });

        setMatchedBankOffers(matched);
      } catch (err) {
        console.error("Error loading product offer:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [productOfferId, navigate]);

  if (loading) return <ProductOfferDetailSkeleton />;
  if (notFound) return <Navigate to="/deal-expired" replace />;
  if (!offer) return null;

  const pageUrl = window.location.href;

  // Handle instructions with line breaks and show more/less toggle
  const instructionsText = offer.instructions?.trim() || "";
  const instructionsLines = instructionsText.split(/\r?\n/);
  const showTruncated = !showFullInstructions && instructionsLines.length > 3;
  const displayedInstructions = showTruncated
    ? instructionsLines.slice(0, 3).join("\n")
    : instructionsText;
  const now = new Date();
  const startDate = offer.startDttm?.toDate ? offer.startDttm.toDate() : offer.startDttm ? new Date(offer.startDttm) : null;
  const endDate = offer.endDttm?.toDate ? offer.endDttm.toDate() : offer.endDttm ? new Date(offer.endDttm) : null;

  return (
    <>
      <div className="max-w-6xl mx-auto p-4 flex flex-col-reverse md:flex-row gap-6 relative">
        {/* Back Button */}
        {!isShared && (
          <div className="absolute top-4 left-4">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
              type="button"
              aria-label="Back"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Details */}
        <div className="flex-1 pt-10 space-y-4">
          <h1 className="text-2xl md:text-3xl font-bold">{offer.productName}</h1>

          <span className="inline-block bg-yellow-50 text-yellow-800 font-semibold px-2 py-0.5 rounded border border-yellow-300 shadow-sm">
            🏷️ {offer.vendor}
          </span>

          {/* Price & Discount */}
          <div className="mb-1 flex items-center flex-wrap gap-2 text-base sm:text-lg font-bold text-indigo-600">
            {!isNaN(Number(offer.offerPrice)) && offer.offerPrice !== "" && (
              <span>${Number(offer.offerPrice).toFixed(2)}</span>
            )}

            {!isNaN(Number(offer.originalPrice)) &&
              Number(offer.originalPrice) > 0 &&
              offer.originalPrice !== "" &&
              offer.originalPrice !== "0.0" && (
                <span className="line-through text-gray-500 text-sm sm:text-base font-semibold">
                  ${Number(offer.originalPrice).toFixed(2)}
                </span>
              )}

            {/* Percentage Off */}
            {offer.originalPrice &&
              offer.offerPrice &&
              Number(offer.originalPrice) > 0 &&
              Number(offer.offerPrice) > 0 &&
              Number(offer.originalPrice) > Number(offer.offerPrice) ? (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded border border-red-300">
                {Math.round(
                  ((Number(offer.originalPrice) - Number(offer.offerPrice)) /
                    Number(offer.originalPrice)) *
                  100
                )}
                % OFF
              </span>
            ) : null}

            {(Number(offer.cashback) > 0 || Number(offer.rebateAmount) > 0) && (
              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded border border-green-300">
                After{" "}
                {Number(offer.cashback) > 0 && Number(offer.rebateAmount) > 0
                  ? `${getRewardLabel(offer.vendor, "cashback")} + ${getRewardLabel(offer.vendor, "rebate")}`
                  : Number(offer.cashback) > 0
                    ? getRewardLabel(offer.vendor, "cashback")
                    : getRewardLabel(offer.vendor, "rebate")}
              </span>
            )}
          </div>

          {/* Promo Code */}
          {offer.promoCode && (
            <div className="mt-2 flex items-center space-x-2 relative">
              <span
                className="text-sm font-semibold text-purple-700 bg-purple-100 px-2 py-1 rounded cursor-pointer select-all"
                title="Click to copy promo code"
                onClick={() => {
                  navigator.clipboard.writeText(offer.promoCode);
                  setCopiedPromo(true);
                  setTimeout(() => setCopiedPromo(false), 2000);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigator.clipboard.writeText(offer.promoCode);
                    setCopiedPromo(true);
                    setTimeout(() => setCopiedPromo(false), 2000);
                  }
                }}
              >
                Promo Code : {offer.promoCode}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(offer.promoCode);
                  setCopiedPromo(true);
                  setTimeout(() => setCopiedPromo(false), 2000);
                }}
                aria-label="Copy promo code"
                className="text-purple-700 hover:text-purple-900 focus:outline-none"
              >
                ✂️
              </button>
              {copiedPromo && (
                <span className="absolute top-full left-0 mt-1 text-xs text-green-600 animate-fade">
                  Copied!
                </span>
              )}
            </div>
          )}

          {offer.offerType && (
            <p className="text-base text-rose-700 font-bold mb-2 select-none">
              {offer.offerType}
            </p>
          )}

          {/* Description */}
          {offer.description && <p className="whitespace-pre-line mb-2">{offer.description}</p>}

          {/* Category & Tags */}
          {offer.category && offer.category.trim() !== "" && (
            <p>
              <strong>Category:</strong> {offer.category}
            </p>
          )}
          {offer.tags && offer.tags.length > 0 && (
            <p>
              <strong>Tags:</strong> {offer.tags.join(", ")}
            </p>
          )}

          {/* Shipping / Pickup */}
          <div className="text-green-600 font-semibold flex gap-2">
            {offer.freeStorePickup?.toLowerCase() === "yes" && <span>🏪 Free Store Pickup</span>}
            {offer.freeStorePickup?.toLowerCase() === "yes" && offer?.freeShippingMinimum === 0 && (
              <span>|</span>
            )}
            {offer?.freeShippingMinimum === 0 && <span>🚚 Free Shipping</span>}
          </div>

          {/* Cashback / Rebate */}
          <div className="flex flex-wrap gap-2 mt-2">
            {Number(offer.cashback) > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-200 text-green-900 border border-green-400">
                💵 {getRewardLabel(offer.vendor, "cashback")}: ${Number(offer.cashback).toFixed(2)}
              </span>
            )}
            {Number(offer.rebateAmount) > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-200 text-red-900 border border-red-400">
                🎁 {getRewardLabel(offer.vendor, "rebate")}: ${Number(offer.rebateAmount).toFixed(2)}
              </span>
            )}
          </div>

          {/* Buy Now / In Store Only */}
          {offer.offerLink ? (
            <>
              <a
                href={offer.offerLink}
                target="_blank"
                rel={trackingId ? "nofollow sponsored noopener noreferrer" : "noopener noreferrer"}
                className="inline-block bg-indigo-600 text-white px-6 py-3 rounded hover:bg-indigo-700 mt-4"
              >
                Buy Now
              </a>
              {trackingId && (
                <p className="text-sm text-gray-500 mt-1">
                  <em>
                    Disclosure: This is an affiliate link. We may earn a commission if you purchase through it.
                  </em>
                </p>
              )}
            </>
          ) : (
            <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-2 rounded font-semibold mt-4 border border-yellow-300">
              In Store Only
            </div>
          )}

          <ShareButtons url={pageUrl} title={offer.productName} offer={offer} />

          {/* Credit Card Offers */}
          {matchedBankOffers.length > 0 && (
            <>
              <h2 className="mt-10 mb-4 text-xl md:text-2xl font-semibold">Credit Card Offers</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {matchedBankOffers.map((bankOffer) => {
                  const bankNameKey = bankOffer.bankName
                    ? bankOffer.bankName.toLowerCase()
                    : bankOffer.vendor?.toLowerCase();
                  const logoUrl = banksLogos[bankNameKey] || "";

                  return (
                    <button
                      key={bankOffer.id}
                      onClick={() => {
                        setSelectedBankOffer(bankOffer);
                        setShowBankOfferPopup(true);
                      }}
                      className="border rounded p-4 flex flex-col items-center cursor-pointer hover:shadow-lg transition"
                      type="button"
                      aria-label={`View details for ${bankOffer.bankName || bankOffer.vendor}`}
                    >
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={bankOffer.bankName || bankOffer.vendor}
                          className="h-14 w-14 object-contain mb-2"
                        />
                      ) : (
                        <div className="h-14 w-14 flex items-center justify-center bg-gray-200 mb-2 text-gray-500">
                          No Logo
                        </div>
                      )}
                      <p className="font-semibold text-center text-xs sm:text-sm truncate w-full">
                        {bankOffer.vendor && !bankOffer.vendor.includes("##") ? bankOffer.vendor : ""}
                      </p>
                      <p className="text-xs text-center mt-1 line-clamp-2">{bankOffer.offer}</p>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>


        {/* Image */}
        {offer.offerImage && (
          <div className="w-full md:w-96 flex justify-center md:justify-end">
            <img
              src={offer.offerImage}
              alt={offer.productName}
              className="w-full max-w-xs md:max-w-xs h-auto max-h-[300px] object-contain rounded-lg shadow"
            />

            {/* Expiry / Upcoming Badge */}
            {(startDate && startDate > now) ? (
              <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-md">
                ⏳ Starts {startDate.toLocaleDateString()}
              </div>
            ) : endDate ? (
              <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-md">
                ⏳ Expires {endDate.toDate ? endDate.toDate().toLocaleDateString() : new Date(endDate).toLocaleDateString()}†
              </div>
            ) : null}

          </div>
        )}
      </div>

      {/* Instructions Section */}
      {instructionsText && (
        <div className="max-w-6xl mx-auto p-4 border-t mt-6">
          <h2 className="text-xl font-semibold mb-2">Instructions</h2>
          <div style={{ whiteSpace: "pre-line" }} className="text-gray-700 text-sm md:text-base">
            <Linkify
              options={{
                target: "_blank",
                rel: "noopener noreferrer",
                className: "text-indigo-600 underline"
              }}
            >
              {displayedInstructions}
            </Linkify>
          </div>
          {instructionsLines.length > 3 && (
            <button
              type="button"
              onClick={() => setShowFullInstructions((prev) => !prev)}
              className="mt-2 text-indigo-600 hover:text-indigo-800 font-semibold"
              aria-expanded={showFullInstructions}
            >
              {showFullInstructions ? "Show Less" : "Show More"}
            </button>
          )}
        </div>
      )}

      {/* Expiry Disclaimer */}
      {offer.endDttm && (
        <div className="max-w-6xl mx-auto p-4">
          <p className="mt-2 text-sm text-gray-500 italic">
            † Item may go out of stock before the expiry date, depending on availability.
          </p>
        </div>
      )}

      {/* Popup */}
      {showBankOfferPopup && selectedBankOffer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ animation: "fadeIn 0.3s ease-in-out" }}
          onClick={() => setShowBankOfferPopup(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full relative shadow-lg animate-fadeInScale"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBankOfferPopup(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
              aria-label="Close bank offer popup"
              type="button"
            >
              ✕
            </button>

            {(() => {
              const bankNameKey = selectedBankOffer.bankName
                ? selectedBankOffer.bankName.toLowerCase()
                : selectedBankOffer.vendor?.toLowerCase();
              const logoUrl = banksLogos[bankNameKey] || "";
              if (logoUrl) {
                return (
                  <img
                    src={logoUrl}
                    alt={selectedBankOffer.bankName || selectedBankOffer.vendor}
                    className="h-20 mx-auto mb-4 object-contain"
                  />
                );
              }
              return null;
            })()}

            <h3 className="text-xl font-bold text-center mb-1">
              {selectedBankOffer.bankName || selectedBankOffer.vendor}
            </h3>
            <p className="text-xl font-semibold text-center mb-1">
              {selectedBankOffer.vendor && !selectedBankOffer.vendor.includes("##")
                ? selectedBankOffer.vendor
                : selectedBankOffer.category}
            </p>
            <p className="mb-2">{selectedBankOffer.offer}</p>

            {selectedBankOffer.expiryDttm && (
              <p className="text-sm text-gray-500 mb-1">
                Expires:{" "}
                {selectedBankOffer.expiryDttm.toDate
                  ? selectedBankOffer.expiryDttm.toDate().toLocaleDateString()
                  : new Date(selectedBankOffer.expiryDttm).toLocaleDateString()}
              </p>
            )}
            {selectedBankOffer.minimumSpend && (
              <p className="text-sm text-gray-600 mb-1">
                Minimum Spend: {selectedBankOffer.minimumSpend}
              </p>
            )}
            {selectedBankOffer.maximumOffer && (
              <p className="text-sm text-gray-600 mb-1">
                Max Discount: {selectedBankOffer.maximumOffer}
              </p>
            )}

            {selectedBankOffer.bankName &&
              banksLogos[selectedBankOffer.bankName.toLowerCase()] && (
                <p className="mt-4 text-center">
                  <a
                    href={
                      selectedBankOffer.bankWebsite ||
                      `https://${selectedBankOffer.bankName.toLowerCase().replace(/\s+/g, "")}.com`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline font-semibold"
                  >
                    Visit Bank Site
                  </a>
                </p>
              )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {opacity: 0;}
          to {opacity: 1;}
        }
        @keyframes fadeInScale {
          0% {opacity: 0; transform: scale(0.9);}
          100% {opacity: 1; transform: scale(1);}
        }
        .animate-fadeInScale {
          animation: fadeInScale 0.25s ease forwards;
        }
          @keyframes fadeOut {
    0% { opacity: 1; }
    90% { opacity: 1; }
    100% { opacity: 0; }
  }
  .animate-fade {
    animation: fadeOut 2s forwards;
  }
      `}</style>
    </>
  );
}
